import React from 'react';
import {useTranslation} from 'react-i18next';
import {Alert, Button, CircularProgress, Grid} from '@mui/material';
import {FormBuilder} from '../../../../../form/FormBuilder';
import {onRejectSubmit} from '../../../../../form/errorHandler';
import schema from './schema/section.json';
import {FormSchema} from '../../../../../form/logic/FormSchema';
import {FormSection} from '../../model/FormSection';
import {FormState} from '../../../../../form/state/FormState';
import {useNotifications} from '../../hooks/useNodifications';
import ResourcePermissionsComponent from '../ResourcePermissionsComponent';
import ExpandableWrapper from '../shared/ExpandableWrapper';
import {useSectionsContext} from '../../contexts/SectionsContext';
import {difference} from '../../../../../form/utils';
import {ResourceType} from '../../model/FormPermission';

interface Props {
  section: FormSection;
}

export default function SectionEditorUpdate({section}: Props) {
  const {t} = useTranslation();
  const {sendSuccess} = useNotifications();
  const {handlePatch, handleDelete, handleExport, handleCopy, locked} = useSectionsContext();

  function onSubmit(formData: any, state: FormState) {
    handlePatch(section.section_id, difference(formData, section)).then(() => sendSuccess(`Session updated`));
  }

  function onDelete() {
    const ok = window.confirm('Are you sure?');
    if (!ok) return;
    handleDelete(section.section_id).then(() => sendSuccess(`Session deleted`));
  }

  function onExport() {
    handleExport(section.section_id).then(() => sendSuccess(`Export completed`));
  }

  function onCopy() {
    handleCopy(section.section_id).then(() => sendSuccess(`Copy completed`));
  }

  return (
    <Grid container spacing={1} rowSpacing={5}>
      <Grid item xs={12}>
        <FormBuilder
          onRejectSubmit={onRejectSubmit}
          formSchema={schema as FormSchema}
          initialValues={section}
          onSubmit={onSubmit}
        >
          <Button disabled={locked} type={'submit'}>
            {t('shared.update')}
          </Button>
          <Button disabled={locked} onClick={onDelete} color={'error'}>
            {t('shared.delete')}
          </Button>
          <Button disabled={locked} onClick={onExport} color={'secondary'}>
            {t('shared.export')}
          </Button>
          <Button disabled={locked} onClick={onCopy} color={'secondary'}>
            {t('shared.copy')}
          </Button>
          {locked && <CircularProgress size={30} sx={{marginLeft: 1}} />}
        </FormBuilder>
      </Grid>
      <Grid item xs={12}>
        <ExpandableWrapper label={'Permissions'}>
          <Alert severity={'warning'} sx={{marginBottom: 2}}>
            Note that <b>sections do not support/require permissions</b> at the moment. You can still add them though,
            we plan to add support in the future.
          </Alert>
          <ResourcePermissionsComponent resourceId={section.section_id} resourceType={ResourceType.SECTION} />
        </ExpandableWrapper>
      </Grid>
    </Grid>
  );
}
